import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import {
  Box,
  Card,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import Page from '../../../components/Page';
import FromAndToDatePicker from '../../../components/mui/FromAndToDatePicker';
import RetentionThemeProvider from '../../retention/theme/RetentionThemeProvider';
import { HERO_GRADIENT, RT } from '../../retention/theme/retentionTheme';
import { EmptyState, IconBadge, SectionCard, SoftChip } from '../../retention/components/ui';
import { getTutorReplacementProjects } from '../../../redux/features/crdMigrationSlice';
import { getTrackerAnalytics } from '../../../redux/features/tutorReplacementTrackerSlice';

// Dashboard → Tutor Replacement Analytics. Open to everyone (CRMs, PMs, …): what happened to the students whose tutor
// was inactivated, per project, per CRO, per TA head (who assigned the case), per TA associate (who worked it), per
// inactivation reason and per tutor. Styled with the CRO retention workflow theme.

// Where a case is: with the project's TA head to assign → with the assignee → retained / tutor change. One colour per
// stage, in the same order in the meters, the legend and the tables.
const STAGES = [
  { key: 'unassigned', label: 'With TA head', hint: 'Not assigned yet', icon: 'solar:crown-minimalistic-bold-duotone', color: '#7C3AED' },
  { key: 'withAssignee', label: 'With associate', hint: 'Assigned, no progress yet', icon: 'solar:user-hand-up-bold-duotone', color: RT.warning },
  { key: 'retained', label: 'Retained', hint: 'Same tutor continues', icon: 'solar:heart-bold-duotone', color: RT.success },
  { key: 'tutorChange', label: 'Tutor change', hint: 'Moved to a new tutor', icon: 'solar:refresh-circle-bold-duotone', color: RT.info },
];

// TA head / associate rows only cover assigned cases, so their "With TA head" column is always 0
const BREAKDOWNS = [
  { key: 'byCro', label: 'CRO wise', nameLabel: 'CRO', icon: 'solar:user-speak-rounded-bold-duotone' },
  { key: 'byTaHead', label: 'TA head wise', nameLabel: 'TA head (assigned by)', icon: 'solar:crown-minimalistic-bold-duotone', assignedOnly: true },
  { key: 'byTaAssociate', label: 'TA associate wise', nameLabel: 'TA associate (assigned to)', icon: 'solar:users-group-rounded-bold-duotone', assignedOnly: true },
];

const ALL_PROJECTS = 'all';
const TOP_RANKS = 3;

// retained out of the cases that have an outcome; '—' until one does
const retentionRate = ({ retained, tutorChange }) => {
  const closed = retained + tutorChange;
  return closed ? `${Math.round((retained / closed) * 100)}%` : '—';
};

const share = (count, total) => (total ? `${Math.round((count / total) * 100)}%` : '0%');

const Legend = () => (
  <Stack direction="row" gap={2} flexWrap="wrap">
    {STAGES.map((stage) => (
      <Stack key={stage.key} direction="row" alignItems="center" gap={0.75}>
        <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: stage.color }} />
        <Typography variant="caption" sx={{ color: RT.ink2, fontWeight: 600 }}>
          {stage.label}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

// Stacked meter of the stages; each segment has a hover tooltip, the numbers sit beside it as text
const StageMeter = ({ row, height = 10 }) => (
  <Stack direction="row" sx={{ height, gap: '2px', borderRadius: 99, overflow: 'hidden', bgcolor: '#EEF0F6' }}>
    {STAGES.filter((stage) => row[stage.key] > 0).map((stage) => (
      <Tooltip key={stage.key} title={`${stage.label}: ${row[stage.key]} of ${row.total}`} arrow>
        <Box sx={{ flex: row[stage.key], bgcolor: stage.color, minWidth: 4, cursor: 'default' }} />
      </Tooltip>
    ))}
  </Stack>
);

const numberCell = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

const Loading = () => <EmptyState compact icon="solar:hourglass-line-bold-duotone" title="Loading…" />;

const RankBadge = ({ rank }) => (
  <Box
    sx={{
      width: 22,
      height: 22,
      flexShrink: 0,
      borderRadius: '7px',
      display: 'grid',
      placeItems: 'center',
      fontSize: 12,
      fontWeight: 800,
      bgcolor: rank <= TOP_RANKS ? RT.primary : RT.primaryLight,
      color: rank <= TOP_RANKS ? '#fff' : RT.primary,
    }}
  >
    {rank}
  </Box>
);

// Compact KPI tile for the 3-per-row grid: number and label on one line, the hint below; long text shows in a tooltip
const KpiTile = ({ icon, label, value, color, hint }) => (
  <Card sx={{ minWidth: 0 }}>
    <Stack direction="row" alignItems="center" gap={1.5} sx={{ px: 2, py: 1.25 }}>
      <IconBadge icon={icon} color={color} size={36} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" alignItems="baseline" gap={1} sx={{ minWidth: 0 }}>
          <Typography sx={{ ...numberCell, fontSize: 20, fontWeight: 800, lineHeight: 1.2, color: RT.ink }}>{value ?? '—'}</Typography>
          <Typography variant="caption" sx={{ color: RT.muted, fontWeight: 700 }} noWrap>
            {label}
          </Typography>
        </Stack>
        {hint && (
          <Tooltip title={hint} placement="bottom-start">
            <Typography variant="caption" component="div" sx={{ color: RT.faint }} noWrap>
              {hint}
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Stack>
  </Card>
);

// One tile per stage with its share of all cases, then the whole split as one meter
function StagePipeline({ summary, loading }) {
  const total = summary?.total ?? 0;
  return (
    <Stack gap={2}>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' } }}>
        {STAGES.map((stage, index) => (
          <Box key={stage.key} sx={{ p: 1.75, borderRadius: 2.5, border: `1px solid ${RT.line}`, borderTop: `3px solid ${stage.color}` }}>
            <Stack direction="row" alignItems="center" gap={1.25}>
              <IconBadge icon={stage.icon} color={stage.color} size={36} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" sx={{ color: RT.muted, fontWeight: 700 }} noWrap>
                  {index + 1}. {stage.label}
                </Typography>
                <Stack direction="row" alignItems="baseline" gap={0.75}>
                  <Typography sx={{ ...numberCell, fontSize: 22, fontWeight: 800, color: RT.ink, lineHeight: 1.2 }}>
                    {loading ? '…' : summary?.[stage.key] ?? 0}
                  </Typography>
                  {!loading && (
                    <Typography variant="caption" sx={{ ...numberCell, color: stage.color, fontWeight: 700 }}>
                      {share(summary?.[stage.key] ?? 0, total)}
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Stack>
            <Typography variant="caption" component="div" sx={{ color: RT.faint, mt: 0.75 }} noWrap>
              {stage.key === 'tutorChange' && summary?.tutorChangeAwaiting
                ? `${summary.tutorChangeAwaiting} still awaiting the new tutor`
                : stage.hint}
            </Typography>
          </Box>
        ))}
      </Box>
      {total > 0 && <StageMeter row={summary} height={12} />}
    </Stack>
  );
}

function ProjectRows({ rows, selected, onSelect }) {
  if (!rows.length) return <EmptyState compact title="No inactivated tutors in this range" />;
  return (
    <Stack divider={<Box sx={{ borderTop: `1px dashed ${RT.line}` }} />}>
      {rows.map((row) => (
        <Box
          key={row.id ?? 'none'}
          onClick={() => row.id && onSelect(row.id)}
          sx={{
            py: 1.5,
            px: 1,
            mx: -1,
            borderRadius: 2,
            cursor: row.id ? 'pointer' : 'default',
            bgcolor: selected === row.id ? RT.primaryLight : 'transparent',
            '&:hover': { bgcolor: row.id ? RT.primaryLight : 'transparent' },
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} gap={{ xs: 1, md: 2.5 }} alignItems={{ md: 'center' }}>
            <Box sx={{ width: { md: 170 }, flexShrink: 0 }}>
              <Typography variant="subtitle2" sx={{ color: RT.ink }}>
                {row.name}
              </Typography>
              <Typography variant="caption" sx={{ color: RT.muted }}>
                {row.total} inactivated · {row.awaitingTutor} awaiting tutor
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <StageMeter row={row} />
            </Box>
            <Stack direction="row" gap={2} flexWrap="wrap" sx={{ flexShrink: 0 }}>
              {STAGES.map((stage) => (
                <Box key={stage.key} sx={{ minWidth: 72 }}>
                  <Typography sx={{ ...numberCell, fontWeight: 800, color: RT.ink, fontSize: 15 }}>{row[stage.key]}</Typography>
                  <Typography variant="caption" sx={{ color: RT.muted }} noWrap>
                    {stage.label}
                  </Typography>
                </Box>
              ))}
              <Box sx={{ minWidth: 64 }}>
                <Typography sx={{ ...numberCell, fontWeight: 800, color: RT.primary, fontSize: 15 }}>{retentionRate(row)}</Typography>
                <Typography variant="caption" sx={{ color: RT.muted }} noWrap>
                  Retention
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

const StageHeadCell = ({ stage }) => (
  <TableCell align="right">
    <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.75} sx={{ whiteSpace: 'nowrap' }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: stage.color }} />
      {stage.label}
    </Stack>
  </TableCell>
);

function BreakdownTable({ rows, nameLabel, assignedOnly }) {
  if (!rows.length) return <EmptyState compact title="Nothing assigned in this range" />;
  const stages = assignedOnly ? STAGES.filter((stage) => stage.key !== 'unassigned') : STAGES;
  return (
    <TableContainer sx={{ maxHeight: 460 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>{nameLabel}</TableCell>
            <TableCell align="right">Total</TableCell>
            {stages.map((stage) => (
              <StageHeadCell key={stage.key} stage={stage} />
            ))}
            <TableCell align="right">Awaiting tutor</TableCell>
            <TableCell align="right">Retention</TableCell>
            <TableCell sx={{ minWidth: 160 }}>Stage split</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id ?? row.name} hover>
              <TableCell sx={{ fontWeight: 700, color: RT.ink }}>{row.name}</TableCell>
              <TableCell align="right" sx={numberCell}>
                {row.total}
              </TableCell>
              {stages.map((stage) => (
                <TableCell key={stage.key} align="right" sx={numberCell}>
                  {row[stage.key]}
                </TableCell>
              ))}
              <TableCell align="right" sx={numberCell}>
                {row.awaitingTutor}
              </TableCell>
              <TableCell align="right" sx={{ ...numberCell, fontWeight: 800, color: RT.primary }}>
                {retentionRate(row)}
              </TableCell>
              <TableCell>
                <StageMeter row={row} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Reasons ranked by inactivations: the bar is the reason's share of all cases, then where those cases are now
function ReasonTable({ rows, total }) {
  if (!rows.length) return <EmptyState compact title="No inactivated tutors in this range" />;
  const max = rows[0].total;
  return (
    <TableContainer sx={{ maxHeight: 520 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Reason</TableCell>
            <TableCell sx={{ minWidth: 200 }}>Inactivations</TableCell>
            {STAGES.map((stage) => (
              <StageHeadCell key={stage.key} stage={stage} />
            ))}
            <TableCell align="right">Retention</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.id ?? row.name} hover>
              <TableCell>
                <Stack direction="row" alignItems="center" gap={1.25}>
                  <RankBadge rank={index + 1} />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: RT.ink }}>
                    {row.name}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" gap={1.25}>
                  <Box sx={{ flex: 1, height: 8, borderRadius: 99, bgcolor: '#EEF0F6', overflow: 'hidden' }}>
                    <Box sx={{ width: `${(row.total / max) * 100}%`, height: '100%', borderRadius: 99, bgcolor: RT.primary }} />
                  </Box>
                  <Typography variant="body2" sx={{ ...numberCell, fontWeight: 800, color: RT.ink, minWidth: 28, textAlign: 'right' }}>
                    {row.total}
                  </Typography>
                  <Typography variant="caption" sx={{ ...numberCell, color: RT.muted, minWidth: 32 }}>
                    {share(row.total, total)}
                  </Typography>
                </Stack>
              </TableCell>
              {STAGES.map((stage) => (
                <TableCell key={stage.key} align="right" sx={numberCell}>
                  {row[stage.key]}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ ...numberCell, fontWeight: 800, color: RT.primary }}>
                {retentionRate(row)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function TutorTable({ rows }) {
  if (!rows.length) return <EmptyState compact title="No inactivated tutors in this range" />;
  return (
    <TableContainer sx={{ maxHeight: 560 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Tutor</TableCell>
            <TableCell align="right">Inactivations</TableCell>
            <TableCell sx={{ minWidth: 260 }}>Reasons</TableCell>
            {STAGES.map((stage) => (
              <StageHeadCell key={stage.key} stage={stage} />
            ))}
            <TableCell align="right">Last inactivated</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.id} hover>
              <TableCell>
                <Stack direction="row" alignItems="center" gap={1.25}>
                  <RankBadge rank={index + 1} />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: RT.ink, whiteSpace: 'nowrap' }}>
                    {row.name}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell align="right" sx={{ ...numberCell, fontWeight: 800, color: RT.ink }}>
                {row.total}
              </TableCell>
              <TableCell>
                <Stack direction="row" gap={0.75} flexWrap="wrap">
                  {row.reasons.map((reason) => (
                    <SoftChip
                      key={reason.name}
                      label={reason.count > 1 ? `${reason.name} ×${reason.count}` : reason.name}
                      color={RT.ink2}
                      sx={{ height: 'auto', py: 0.25, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                    />
                  ))}
                </Stack>
              </TableCell>
              {STAGES.map((stage) => (
                <TableCell key={stage.key} align="right" sx={numberCell}>
                  {row[stage.key]}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ ...numberCell, color: RT.muted }}>
                {row.lastInactivatedOn ? dayjs(row.lastInactivatedOn).format('DD MMM YYYY') : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function TutorReplacementAnalytics() {
  const dispatch = useDispatch();
  const [fromDate, setFromDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(ALL_PROJECTS);
  const [breakdown, setBreakdown] = useState(BREAKDOWNS[0].key);
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    const promise = dispatch(getTutorReplacementProjects());
    promise.then((action) => {
      if (!action.meta?.aborted) setProjects(action.payload?.data?.projects ?? []);
    });
    return () => promise.abort();
  }, [dispatch]);

  useEffect(() => {
    setState((prev) => ({ ...prev, loading: true }));
    const params = { from: fromDate, to: toDate, projectId: projectId === ALL_PROJECTS ? '' : projectId };
    // a newer filter change cancels the request still in flight
    const promise = dispatch(getTrackerAnalytics(params));
    promise.then((action) => {
      if (action.meta?.aborted) return;
      setState({ loading: false, data: action.payload?.status === 'success' ? action.payload.data : null });
    });
    return () => promise.abort();
  }, [dispatch, fromDate, toDate, projectId]);

  const { loading, data } = state;
  const summary = data?.summary;
  const reasons = data?.byReason ?? [];
  const tutors = data?.byTutor ?? [];
  const tiles = useMemo(
    () => [
      { label: 'Tutors inactivated', key: 'total', icon: 'solar:user-cross-rounded-bold-duotone', color: RT.primary, hint: 'Tutor-reason inactivations' },
      { label: 'Pending', key: 'pending', icon: 'solar:clock-circle-bold-duotone', color: RT.warning, hint: 'With TA head + associate' },
      { label: 'Awaiting new tutor', key: 'awaitingTutor', icon: 'solar:hourglass-line-bold-duotone', color: RT.error, hint: 'No tutor on the subject yet' },
      { label: 'New tutor assigned', key: 'newTutorAssigned', icon: 'solar:user-check-rounded-bold-duotone', color: RT.success, hint: 'A later tutor is mapped' },
    ],
    [],
  );
  const active = BREAKDOWNS.find((item) => item.key === breakdown);
  const show = (value) => (loading ? '…' : value);
  const firstLoad = loading && !data;

  return (
    <RetentionThemeProvider>
      <Page title="Tutor Replacement Analytics">
        <Stack gap={2.5}>
          <Card sx={{ overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" gap={2} sx={{ p: 2.5, background: HERO_GRADIENT, color: '#fff' }}>
              <IconBadge icon="solar:user-id-bold-duotone" color="#fff" size={48} sx={{ bgcolor: 'rgba(255,255,255,.18)', color: '#fff' }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="overline" sx={{ opacity: 0.8 }}>
                  Dashboard
                </Typography>
                <Typography variant="h4" sx={{ color: '#fff' }}>
                  Tutor Replacement Analytics
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Inactivated tutors and what the TA wing did next — retained or tutor changed
                </Typography>
              </Box>
            </Stack>
          </Card>

          <Card>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} gap={1.5} flexWrap="wrap" sx={{ p: 2 }}>
              <TextField
                select
                size="small"
                label="Project"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                sx={{ minWidth: { xs: '100%', sm: 200 } }}
              >
                <MenuItem value={ALL_PROJECTS}>All projects</MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </TextField>
              <FromAndToDatePicker fromDate={fromDate} toDate={toDate} setFromDate={setFromDate} setToDate={setToDate} disabled={loading} />
            </Stack>
          </Card>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } }}>
            {tiles.map((tile) => (
              <KpiTile
                key={tile.key}
                icon={tile.icon}
                color={tile.color}
                label={tile.label}
                hint={tile.hint}
                value={show(summary?.[tile.key] ?? 0)}
              />
            ))}
            <KpiTile
              icon="solar:chart-2-bold-duotone"
              color={RT.primaryDark}
              label="Retention rate"
              hint="Retained ÷ (retained + tutor change)"
              value={show(summary ? retentionRate(summary) : '—')}
            />
            <KpiTile
              icon="solar:danger-triangle-bold-duotone"
              color={RT.error}
              label="Top reason"
              hint={reasons[0]?.name ?? 'No inactivations'}
              value={show(reasons[0]?.total ?? '—')}
            />
          </Box>

          <SectionCard icon="solar:routing-2-bold-duotone" title="Case stages">
            <StagePipeline summary={summary} loading={loading} />
          </SectionCard>

          <SectionCard icon="solar:buildings-2-bold-duotone" title="Project wise" action={<Legend />}>
            {firstLoad ? <Loading /> : <ProjectRows rows={data?.byProject ?? []} selected={projectId} onSelect={setProjectId} />}
          </SectionCard>

          <SectionCard
            icon={active.icon}
            title={active.label}
            action={
              <ToggleButtonGroup size="small" exclusive value={breakdown} onChange={(_, value) => value && setBreakdown(value)}>
                {BREAKDOWNS.map((item) => (
                  <ToggleButton key={item.key} value={item.key}>
                    {item.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            }
          >
            {firstLoad ? (
              <Loading />
            ) : (
              <BreakdownTable rows={data?.[breakdown] ?? []} nameLabel={active.nameLabel} assignedOnly={active.assignedOnly} />
            )}
          </SectionCard>

          <SectionCard icon="solar:clipboard-list-bold-duotone" color={RT.error} title="Reason wise (most frequent first)">
            {firstLoad ? <Loading /> : <ReasonTable rows={reasons} total={summary?.total ?? 0} />}
          </SectionCard>

          <SectionCard
            icon="solar:user-block-bold-duotone"
            color={RT.error}
            title="Most inactivated tutors"
            action={
              !firstLoad &&
              data?.tutorCount > tutors.length && (
                <Typography variant="caption" sx={{ color: RT.muted, fontWeight: 600 }}>
                  Top {tutors.length} of {data.tutorCount} tutors
                </Typography>
              )
            }
          >
            {firstLoad ? <Loading /> : <TutorTable rows={tutors} />}
          </SectionCard>
        </Stack>
      </Page>
    </RetentionThemeProvider>
  );
}
